import React from 'react';

import { CONFIRMATION_MODALS_TITLES } from 'pages/ExperienceCanvas/types';
import { getWarningOnLaunchScheduled } from 'helpers/templated-experience.helper';
import { Experience } from 'interface/experience/experience.interface';

/**
 * Pure, framework-agnostic builders for the transactional confirm dialogs surfaced by the v2
 * journey action layer ({@link useJourneyActions}). Each builder returns the *content* of a
 * dialog — title, body, optional icon and the primary/secondary button labels — but does **not**
 * own visibility or wire any callbacks. The hook drives them through an await-based
 * `showConfirmModal()`, which is what lets v2 replace v1's callback-stuffed
 * `primaryButton.onClick` closures with linear `async`/`await` control flow.
 *
 * Mirrors the dialog copy previously inlined into v1's `PrimaryHeader` handlers.
 */
export type ConfirmDialog = {
  title: string;
  content: React.ReactNode;
  contentIcon?: React.ReactElement;
  /** Label for the confirming (primary) button. */
  confirmLabel: string;
  /** Label for the dismissing (cancel) button, when the dialog is a true confirm/cancel pair. */
  cancelLabel?: string;
  /** Forwarded to {@link ConfirmModal} to surface the empty-audience warning + checkbox. */
  isLaunching?: boolean;
  isAudienceEmpty?: boolean;
};

type ScheduleFutureParams = {
  schedule?: Experience['schedule'];
  isListBasedTemplate: boolean;
  hasPendingFiles: boolean;
  isAudienceEmpty: boolean;
};

/**
 * Shown when the user launches an experience whose schedule starts in the future. Confirming
 * saves the instance as `SCHEDULED`.
 */
export function buildScheduleFutureDialog({
  schedule,
  isListBasedTemplate,
  hasPendingFiles,
  isAudienceEmpty,
}: ScheduleFutureParams): ConfirmDialog {
  const { startDate, endDate, newTitle, newMessage } = getWarningOnLaunchScheduled(schedule);

  return {
    title: newTitle,
    isLaunching: true,
    isAudienceEmpty,
    confirmLabel: 'Confirm',
    content: (
      <>
        <p>{newMessage}</p>
        <p>
          <strong>Start Date: </strong> {startDate}
        </p>
        <p>
          <strong>End Date: </strong> {endDate}
        </p>
        {isListBasedTemplate && hasPendingFiles ? (
          <p>
            <strong>This Journey has at least one uploaded file that has not been processed yet.</strong>
          </p>
        ) : null}
      </>
    ),
  };
}

/**
 * Shown when the user launches an experience whose schedule has already expired. Informational
 * only — the confirming action simply closes the dialog (v1's "Edit Schedule" was a no-op TODO).
 */
export function buildExpiredScheduleDialog(schedule?: Experience['schedule']): ConfirmDialog {
  return {
    title: 'Expired Schedule',
    confirmLabel: 'Edit Schedule',
    content: (
      <>
        <p>You can&apos;t launch an experience with an expired schedule</p>
        <p>
          To launch, edit your schedule {!schedule?.start ? 'to add start date' : 'to a current date/time'}
        </p>
      </>
    ),
  };
}

/**
 * Confirm/cancel dialog for deactivating a live or scheduled experience.
 */
export function buildDeactivateDialog(contentIcon?: React.ReactElement): ConfirmDialog {
  return {
    title: CONFIRMATION_MODALS_TITLES.DEACTIVATE_EXPERIENCE,
    contentIcon,
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    content: (
      <>
        <p>You are about to deactivate your experience immediately.</p>
        <p>Once deactivated, it will no longer be visible to your customers according to the schedule.</p>
        <p>Are you sure you would like to deactivate it?</p>
      </>
    ),
  };
}

/**
 * Shown on save when a scheduled experience has no schedule attached — confirming flips it to
 * `DRAFT` so a subsequent save launches immediately.
 */
export function buildLaunchImmediatelyDialog(contentIcon?: React.ReactElement): ConfirmDialog {
  return {
    title: CONFIRMATION_MODALS_TITLES.LAUNCH_IMMEDIATELY,
    contentIcon,
    confirmLabel: 'Okay',
    content: (
      <>
        <p>This experience does not have a schedule associated with it.</p>
        <p>
          As a result it will launch <strong>immediately</strong> and be visible to your customers.
        </p>
        <p>
          If you wish to add a schedule then click on &quot;<strong>Cancel</strong>&quot; and add a schedule.
        </p>
      </>
    ),
  };
}

/**
 * Shown on save when an active/scheduled experience can no longer be edited.
 */
export function buildCantEditActiveDialog(activationDate: string, contentIcon?: React.ReactElement): ConfirmDialog {
  return {
    title: CONFIRMATION_MODALS_TITLES.CANT_EDIT_ACTIVE_EXPERIENCE,
    contentIcon,
    confirmLabel: 'Okay',
    content: (
      <>
        <p>
          Your experience has been active since <strong>{activationDate}</strong>
        </p>
        <p>
          <strong>Live experiences cannot be edited.</strong>
        </p>
        <p>Please Deactivate the experience to make changes to it.</p>
      </>
    ),
  };
}

/**
 * Shown on save when an inactive experience is still invalid.
 */
export function buildCantSaveInvalidDialog(contentIcon?: React.ReactElement): ConfirmDialog {
  return {
    title: CONFIRMATION_MODALS_TITLES.CANT_SAVE_INVALID_EXPERIENCE,
    contentIcon,
    confirmLabel: 'Okay',
    content: (
      <>
        <p>
          <strong>Inactive experiences must be valid to be saved.</strong>
        </p>
        <p>Please resolve all errors before saving.</p>
        <p>Touchpoint errors can be seen by clicking the status footers of their respective cards.</p>
        <p>
          If present, other errors can be seen by clicking the error icon on the top left area of the card canvas.
        </p>
      </>
    ),
  };
}
